import { Font, Document as PDFDoc, Page as PDFPage, StyleSheet, View } from '@react-pdf/renderer';

import { MeasurementData } from '../../../store/reducers/workspace.reducer.ts';
import { Project, Version } from '../../../types/project.ts';
import { Norm } from '../../../types/user.ts';
import type { UnitSystem } from '../../../types/util.ts';
import { ExpectedActualPair, isTargetSatisfied } from '../../../utils/normSatisfactionUtils.ts';
import {
  type LuminairePdfDisplay,
  formatLuminaireLumenOutput,
  formatLuminairePdfHeader,
  formatLuminairePdfName,
} from '../../../utils/pdfLuminaireDisplayFormat';
import {
  PDF_PHOTOMETRY_ENTRIES,
  type PhotometrySelection,
  isPhotometryIncluded,
} from '../../../utils/pdfPlotGeneration/photometryMapping.ts';
import {
  createLayerTitle,
  getAvailablePhotometryTypes,
  getCurrentFormattedDate,
  getIlluminatedLayers,
  getLuminaireLdcImages,
} from '../../../utils/pdfUtils';
import { formatLength, getLengthUnitLabel, getPhotometricUnit } from '../../../utils/unitSystem.ts';
import Footer from './Footer.tsx';
import Header from './Header.tsx';
import { PDF_DOCUMENT_CONSTANTS } from './constants/pdfDocumentConstants.ts';
import Homepage from './content/Homepage.tsx';
import TableOfContent from './content/TableOfContent.tsx';
import GlobalPlots from './content/global/GlobalPlots.tsx';
import LampDetails from './content/lamps/LampDetails.tsx';
import LayerDetails from './content/layers/LayerDetails.tsx';
import Summary from './content/layers/Summary.tsx';
import { LampData, LayerDetailsData } from './pdfData';

interface ContentPage {
  id: string;
  title: string;
  component: React.ComponentType;
  isTocEntry?: boolean;
  chapterNumber: string;
}

interface TocEntry {
  id: string;
  title: string;
  pageNumber: number;
  chapterNumber: string;
}

interface PdfDocumentProps {
  checkedItems: Record<string, boolean>;
  checkedMeasurementsItems: Record<string, boolean>;
  project: Project;
  activeVersion: Version;
  norms: Norm[];
  reportImageUrls: Record<string, string>;
  frontendReportImageUrls?: Record<string, string>;
  luminaireDisplayByUuid?: Record<string, LuminairePdfDisplay>;
  measurementData: MeasurementData[];
  unitSystem: UnitSystem;
  /** Photometries to include; plots and table rows of the others are left out. */
  selectedPhotometries?: PhotometrySelection;
}

// Register STIX Two Math font for Unicode symbols (subscripts, combining diacritics)
// STIX Two Math is specifically designed for mathematical notation
Font.register({
  family: 'STIXTwoMath',
  fonts: [
    {
      src: '/fonts/STIXTwoMath-Regular.woff',
      fontWeight: 400,
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    fontSize: PDF_DOCUMENT_CONSTANTS.PAGE.FONT_SIZE,
    paddingTop: PDF_DOCUMENT_CONSTANTS.PAGE.PADDING_TOP,
    paddingBottom: PDF_DOCUMENT_CONSTANTS.PAGE.PADDING_BOTTOM,
  },
});

export const PdfDocument = ({
  checkedItems,
  checkedMeasurementsItems,
  project,
  activeVersion,
  norms,
  reportImageUrls,
  frontendReportImageUrls,
  luminaireDisplayByUuid = {},
  measurementData,
  unitSystem,
  selectedPhotometries,
}: PdfDocumentProps) => {
  const lengthUnitLabel = getLengthUnitLabel(unitSystem);
  const dynamicReportData = {
    reportTitle: project.name,
    date: getCurrentFormattedDate(),
    description: project.description,
    imagePath: project.previewImageUrl,
  };

  const lampsData =
    !checkedItems[PDF_DOCUMENT_CONSTANTS.SECTIONS.LAMP_INFO] || !activeVersion.lights
      ? []
      : [...activeVersion.lights]
          .sort((a, b) => {
            const aDisplay = luminaireDisplayByUuid[a.uuid]?.displayName ?? a.name ?? '';
            const bDisplay = luminaireDisplayByUuid[b.uuid]?.displayName ?? b.name ?? '';
            return aDisplay.localeCompare(bDisplay, undefined, { sensitivity: 'base' });
          })
          .map((light, index) => {
            const display: LuminairePdfDisplay = luminaireDisplayByUuid[light.uuid] ?? {
              manufacturer: PDF_DOCUMENT_CONSTANTS.LAMP.MANUFACTURER,
              displayName: light.name,
              lumenOutput: light.lumenOutput,
            };
            const shortId = light.uuid.substring(0, 8);

            return {
              firstPage: index === 0,
              header: formatLuminairePdfHeader(display, shortId),
              details: {
                leftColumn: [
                  { label: PDF_DOCUMENT_CONSTANTS.LABELS.MANUFACTURER, value: display.manufacturer },
                  { label: PDF_DOCUMENT_CONSTANTS.LABELS.LAMP_ID, value: shortId },
                  { label: PDF_DOCUMENT_CONSTANTS.LABELS.LAMP_NAME, value: formatLuminairePdfName(display) },
                  {
                    label: PDF_DOCUMENT_CONSTANTS.LABELS.LUMEN_OUTPUT,
                    value: formatLuminaireLumenOutput(display.lumenOutput ?? light.lumenOutput),
                  },
                  {
                    label: `${PDF_DOCUMENT_CONSTANTS.LABELS.HEIGHT} (${lengthUnitLabel})`,
                    value: formatLength(light.distanceToGround, unitSystem),
                  },
                ],
                rightColumn: [
                  { label: PDF_DOCUMENT_CONSTANTS.LABELS.LATITUDE, value: light.latitude || '--' },
                  { label: PDF_DOCUMENT_CONSTANTS.LABELS.LONGITUDE, value: light.longitude || '--' },
                  {
                    label: `${PDF_DOCUMENT_CONSTANTS.LABELS.POSITION_X} (${lengthUnitLabel})`,
                    value: formatLength(light.positionX, unitSystem),
                  },
                  {
                    label: `${PDF_DOCUMENT_CONSTANTS.LABELS.POSITION_Y} (${lengthUnitLabel})`,
                    value: formatLength(light.positionY, unitSystem),
                  },
                  {
                    label: `${PDF_DOCUMENT_CONSTANTS.LABELS.POSITION_Z} (${lengthUnitLabel})`,
                    value: formatLength(light.positionZ, unitSystem),
                  },
                ],
              },
              images: getLuminaireLdcImages(light.uuid, activeVersion, reportImageUrls, frontendReportImageUrls),
            };
          });

  const illuminatedLayers = getIlluminatedLayers(activeVersion);

  // Target functions of deselected photometries (e.g. 'Ev'); ratio rows (Uo, fTI, …) always stay.
  const excludedTargetFns = new Set<string>(
    PDF_PHOTOMETRY_ENTRIES.filter((e) => !isPhotometryIncluded(selectedPhotometries, e.photometry)).map(
      (e) => e.targetFn,
    ),
  );
  const selectedImages = (images: ReturnType<typeof getAvailablePhotometryTypes>) =>
    images.filter((image) => isPhotometryIncluded(selectedPhotometries, image.photometry));

  const getPhotometryPairs = (layerUuid: string) => {
    if (!measurementData) return [];
    const layerMeasurement = measurementData.find((m) => m.uuid === layerUuid);
    if (!layerMeasurement) return [];

    const pairs: ExpectedActualPair[] = [];
    for (const expected of layerMeasurement.expectedValues) {
      const actual = layerMeasurement.actualValues.find(
        (a) => a.function === expected.function && a.type === expected.type,
      );
      if (actual && !excludedTargetFns.has(expected.function)) {
        pairs.push({
          expected: expected.value,
          actual: actual.value,
          function: expected.function,
          type: expected.type,
        });
      }
    }
    return pairs;
  };

  const summaryData = !checkedItems[PDF_DOCUMENT_CONSTANTS.SECTIONS.LAYERS_SUMMARY]
    ? []
    : illuminatedLayers
        .filter((mesh) => {
          const layerTitle = createLayerTitle(
            mesh.displayName,
            mesh.normId,
            norms,
            mesh.targets?.length ? 'custom' : undefined,
          );
          return checkedMeasurementsItems[layerTitle];
        })
        .map((mesh) => {
          const pairs = getPhotometryPairs(mesh.uuid);

          const tableRowBody = pairs.map((pair) => ({
            symbol: pair.function,
            type: pair.type,
            calculated: pair.actual,
            target: pair.expected,
            unit: getPhotometricUnit(pair.function) || '',
            check: isTargetSatisfied(pair),
          }));

          return {
            groupHeader: createLayerTitle(
              mesh.displayName,
              mesh.normId,
              norms,
              mesh.targets?.length ? 'custom' : undefined,
            ),
            tableRowBody,
          };
        })
        .filter((group) => group.tableRowBody.length > 0);

  const layerDetailsData = !checkedItems[PDF_DOCUMENT_CONSTANTS.SECTIONS.GRAPHS]
    ? []
    : illuminatedLayers
        .filter((mesh) => {
          const layerTitle = createLayerTitle(
            mesh.displayName,
            mesh.normId,
            norms,
            mesh.targets?.length ? 'custom' : undefined,
          );
          return checkedMeasurementsItems[layerTitle];
        })
        .map((mesh, index) => {
          const layerTitle = createLayerTitle(
            mesh.displayName,
            mesh.normId,
            norms,
            mesh.targets?.length ? 'custom' : undefined,
          );
          const pairs = getPhotometryPairs(mesh.uuid);
          const metrics = pairs.map((pair) => ({
            symbol: pair.function,
            type: pair.type,
            calculated: pair.actual,
            target: pair.expected,
            unit: getPhotometricUnit(pair.function) || '',
            passed: isTargetSatisfied(pair),
          }));

          const dynamicLayerDetail: LayerDetailsData = {
            id: mesh.uuid,
            title: layerTitle,
            headerDescription: PDF_DOCUMENT_CONSTANTS.MEASUREMENTS.MAINTENANCE_FACTOR_DESCRIPTION,
            tableRows: [
              {
                groupTitle: layerTitle,
                metrics,
              },
            ],
            images: selectedImages(
              getAvailablePhotometryTypes(mesh.uuid, activeVersion, reportImageUrls, frontendReportImageUrls),
            ),
          };

          return {
            ...dynamicLayerDetail,
            index: index + 1,
          };
        });

  const globalPlotsImages = !checkedItems[PDF_DOCUMENT_CONSTANTS.SECTIONS.GLOBAL_PLOTS]
    ? []
    : selectedImages(getAvailablePhotometryTypes('global', activeVersion, reportImageUrls, frontendReportImageUrls));

  // Build document content synchronously
  const documentContent: ContentPage[] = [];
  let mainChapterNumber = 1;

  if (checkedItems[PDF_DOCUMENT_CONSTANTS.SECTIONS.COVER_PAGE]) {
    documentContent.push({
      id: PDF_DOCUMENT_CONSTANTS.IDS.COVER_PAGE,
      title: PDF_DOCUMENT_CONSTANTS.TITLES.COVER,
      component: () => <Homepage homepageData={dynamicReportData} />,
      isTocEntry: false,
      chapterNumber: `${mainChapterNumber}.`,
    });
    mainChapterNumber++;
  }

  if (checkedItems[PDF_DOCUMENT_CONSTANTS.SECTIONS.TABLE_OF_CONTENT]) {
    documentContent.push({
      id: PDF_DOCUMENT_CONSTANTS.IDS.TABLE_OF_CONTENT,
      title: PDF_DOCUMENT_CONSTANTS.TITLES.CONTENT,
      component: () => null,
      isTocEntry: false,
      chapterNumber: `${mainChapterNumber}.`,
    });
    mainChapterNumber++;
  }

  if (checkedItems[PDF_DOCUMENT_CONSTANTS.SECTIONS.LAMP_INFO]) {
    lampsData.forEach((lamp: LampData, index: number) => {
      const chapterNumber = `${mainChapterNumber}.${index + 1}.`;

      documentContent.push({
        id: `${PDF_DOCUMENT_CONSTANTS.IDS.LAMP_INFO_PREFIX}${index}`,
        title: lamp.header,
        component: () => <LampDetails lampData={lamp} />,
        isTocEntry: true,
        chapterNumber: chapterNumber,
      });
    });
    if (lampsData.length > 0) {
      mainChapterNumber++;
    }
  }

  if (checkedItems[PDF_DOCUMENT_CONSTANTS.SECTIONS.LAYERS_SUMMARY]) {
    documentContent.push({
      id: PDF_DOCUMENT_CONSTANTS.IDS.LAYERS_SUMMARY,
      title: PDF_DOCUMENT_CONSTANTS.TITLES.LAYERS_SUMMARY,
      component: () => <Summary summaryData={summaryData} />,
      isTocEntry: true,
      chapterNumber: `${mainChapterNumber}.`,
    });
    mainChapterNumber++;
  }

  if (checkedItems[PDF_DOCUMENT_CONSTANTS.SECTIONS.GRAPHS]) {
    layerDetailsData.forEach((layerDetail) => {
      const chapterNumber = `${mainChapterNumber}.${layerDetail.index}.`;

      documentContent.push({
        id: `${PDF_DOCUMENT_CONSTANTS.IDS.GRAPHS_PREFIX}${layerDetail.index}`,
        title: layerDetail.title,
        component: () => <LayerDetails layerDetailsData={layerDetail} />,
        isTocEntry: true,
        chapterNumber: chapterNumber,
      });
    });
    if (layerDetailsData.length > 0) {
      mainChapterNumber++;
    }
  }

  if (checkedItems[PDF_DOCUMENT_CONSTANTS.SECTIONS.GLOBAL_PLOTS] && globalPlotsImages.length > 0) {
    documentContent.push({
      id: PDF_DOCUMENT_CONSTANTS.IDS.GLOBAL_PLOTS,
      title: PDF_DOCUMENT_CONSTANTS.TITLES.GLOBAL_PLOTS,
      component: () => <GlobalPlots title={PDF_DOCUMENT_CONSTANTS.TITLES.GLOBAL_PLOTS} images={globalPlotsImages} />,
      isTocEntry: true,
      chapterNumber: `${mainChapterNumber}.`,
    });
    mainChapterNumber++;
  }

  // Calculate final pages and table of contents synchronously
  const tocEntries = documentContent.filter((p) => p.isTocEntry);

  const tableOfContents: TocEntry[] = tocEntries.map((page, index) => {
    const offset =
      (checkedItems[PDF_DOCUMENT_CONSTANTS.SECTIONS.COVER_PAGE]
        ? PDF_DOCUMENT_CONSTANTS.PAGE_OFFSET.COVER_PAGE_OFFSET
        : 0) +
      (checkedItems[PDF_DOCUMENT_CONSTANTS.SECTIONS.TABLE_OF_CONTENT]
        ? PDF_DOCUMENT_CONSTANTS.PAGE_OFFSET.TABLE_OF_CONTENT_OFFSET
        : 0);

    return {
      id: page.id,
      title: page.title,
      pageNumber: index + 1 + offset,
      chapterNumber: page.chapterNumber,
    };
  });

  const finalPages: ContentPage[] = [];

  if (checkedItems[PDF_DOCUMENT_CONSTANTS.SECTIONS.COVER_PAGE]) {
    const coverPage = documentContent.find((p) => p.id === PDF_DOCUMENT_CONSTANTS.IDS.COVER_PAGE);
    if (coverPage) {
      finalPages.push(coverPage);
    }
  }

  if (checkedItems[PDF_DOCUMENT_CONSTANTS.SECTIONS.TABLE_OF_CONTENT]) {
    finalPages.push({
      id: PDF_DOCUMENT_CONSTANTS.IDS.TABLE_OF_CONTENT,
      title: PDF_DOCUMENT_CONSTANTS.TITLES.CONTENT,
      component: () => (
        <TableOfContent
          finalPages={finalPages}
          pagesData={tableOfContents}
          pageNumbers={tableOfContents.reduce((acc, curr) => ({ ...acc, [curr.id]: curr.pageNumber }), {})}
        />
      ),
      isTocEntry: false,
      chapterNumber: PDF_DOCUMENT_CONSTANTS.CHAPTERS.TABLE_OF_CONTENT,
    });
  }

  tocEntries.forEach((page) => {
    finalPages.push(page);
  });

  const pagesToRender = finalPages;

  return (
    <PDFDoc>
      {pagesToRender.map((page) => {
        const PageComponent = page.component;

        return (
          <PDFPage key={page.id} size={PDF_DOCUMENT_CONSTANTS.PAGE.SIZE} style={styles.page} wrap>
            <Header project={project} activeVersion={activeVersion} />
            <View>
              {page.id === PDF_DOCUMENT_CONSTANTS.IDS.TABLE_OF_CONTENT ? (
                <TableOfContent
                  finalPages={finalPages}
                  pagesData={tableOfContents}
                  pageNumbers={tableOfContents.reduce((acc, curr) => ({ ...acc, [curr.id]: curr.pageNumber }), {})}
                />
              ) : (
                <PageComponent />
              )}
            </View>
            <Footer />
          </PDFPage>
        );
      })}
    </PDFDoc>
  );
};
